import { defineStore } from 'pinia'
import { ref, reactive } from 'vue'
import { HomeAPI } from '@/services/home'
import type { RoleList } from '@/types/api'

type TabKey = number | string

interface TabState<T> {
  items: T[]
  page: number
  pageSize: number
  hasMore: boolean
  loading: boolean
  loadingMore: boolean
  initialized: boolean
  scrollTop: number
  lastLoadedAt: number
  hasError: boolean // 🔧 新增：是否有网络错误或接口错误
}

const createInitialTabState = <T>(): TabState<T> => ({
  items: [],
  page: 1,
  pageSize: 20,
  hasMore: true,
  loading: true,
  loadingMore: false,
  initialized: false,
  scrollTop: 0,
  lastLoadedAt: 0,
  hasError: false // 🔧 初始化错误状态
})

export const useWaterfallStore = defineStore('waterfall', () => {
  const tabNavs = ref<any[]>([
    { title: 'Hot' },
    { title: 'Popular' },
    { title: 'New' },
    { title: 'Female' }
  ])
  const activeTab = ref<TabKey>(0)
  const tabStates = reactive<Record<string, TabState<RoleList>>>({})

  const ensureTab = (tab: TabKey) => {
    const key = String(tab)
    if (!tabStates[key]) {
      tabStates[key] = createInitialTabState<RoleList>()
    }
    return tabStates[key]
  }

  const setActiveTab = (tab: TabKey) => {
    activeTab.value = tab
    ensureTab(tab)
  }

  const getCurrentState = () => ensureTab(activeTab.value)

  const saveScrollPosition = (scrollTop: number, tab: TabKey = activeTab.value) => {
    ensureTab(tab).scrollTop = scrollTop
  }

  const getScrollPosition = (tab: TabKey = activeTab.value) => ensureTab(tab).scrollTop

  const shouldRefresh = (tab: TabKey = activeTab.value) => !ensureTab(tab).initialized

  const fetchPage = async (id: number, page: number, pageSize: number): Promise<RoleList[]> => {
    const res = await HomeAPI.getRoleList({
      page,
      pageSize,
      type: id,
      isSimple:true
    })
    return (res?.data as unknown as RoleList[]) ?? []
  }

  const refresh = async (tab: TabKey = activeTab.value, id: number, isLoadingLogin?: boolean) => {
    const state = ensureTab(tab)
    if (isLoadingLogin) {
      state.loading = true
    }
    
    if (!state.loading) return
    try {
      state.page = 1
      const data = await fetchPage(id, state.page, state.pageSize)
      state.items = data
      state.hasMore = data.length >= state.pageSize
      state.initialized = true
      state.lastLoadedAt = Date.now()
      state.hasError = false // 🔧 成功时清除错误状态
    } catch (error) {
      console.error('刷新失败:', error)
      state.hasError = true // 🔧 失败时设置错误状态
      throw error
    } finally {
      state.loading = false
    }
  }

  const loadMore = async (tab: TabKey = activeTab.value, id: number) => {
    const state = ensureTab(tab)
    if (state.loadingMore || !state.hasMore) return
    state.loadingMore = true
    try {
      const nextPage = state.page + 1
      const data = await fetchPage(id, nextPage, state.pageSize)
      state.items = state.items.concat(data)
      state.page = nextPage
      state.hasMore = data.length >= state.pageSize
      state.lastLoadedAt = Date.now()
      // 🔧 注意：loadMore 失败不设置 hasError，因为已有数据，只是加载更多失败
    } catch (error) {
      console.error('加载更多失败:', error)
      // 加载更多失败不影响现有数据，所以不设置 hasError
    } finally {
      state.loadingMore = false
    }
  }

  const refreshCurrentTab = async (tab: TabKey, id: number) => {
    const state = ensureTab(tab)
    state.page = 1
    state.hasMore = true
    // 注意：这里不设置 loading = true，避免触发骨架屏
    try {
      const data = await fetchPage(id, 1, state.pageSize)
      state.items = data
      state.hasMore = data.length >= state.pageSize
      state.initialized = true
      state.lastLoadedAt = Date.now()
      state.hasError = false // 🔧 成功时清除错误状态
    } catch (error) {
      console.error('刷新失败:', error)
      state.hasError = true // 🔧 失败时设置错误状态
      throw error
    }
  }

  const switchTab = async (tab: TabKey, id: number) => {

    setActiveTab(tab)
    // 如果tab未初始化，则刷新数据
    // if (!state.initialized) {
    // }
    await refresh(tab, id)

    // 注意：已初始化的tab会保持其数据状态，包括滚动位置
  }

  const setPageSize = (size: number, tab: TabKey = activeTab.value) => {
    ensureTab(tab).pageSize = size
  }

  const replaceItems = (items: RoleList[], tab: TabKey = activeTab.value) => {
    const state = ensureTab(tab)
    state.items = items
    state.page = 1
    state.hasMore = items.length >= state.pageSize
    state.initialized = true
    state.lastLoadedAt = Date.now()
  }

  const removeItem = (tab: TabKey = activeTab.value) => {
    const state = ensureTab(tab)
    state.items = []
  }

  /**
   * 获取角色分类列表
   */
  const getTabNavs = async () => {
    const response = await HomeAPI.getRoleTypeList({})
    tabNavs.value = response.data
  }

  /**
   * 清理所有tab数据（用于退出登录或删除账号）
   */
  const clearAllData = () => {
    // 重置activeTab
    activeTab.value = 1

    // 清空所有tab状态
    Object.keys(tabStates).forEach(key => {
      delete tabStates[key]
    })
  }

  return {
    // state
    activeTab,
    tabStates,
    tabNavs,

    // queries
    ensureTab,
    getCurrentState,
    getScrollPosition,
    shouldRefresh,

    // mutations/actions
    setActiveTab,
    setPageSize,
    saveScrollPosition,
    replaceItems,
    refresh,
    loadMore,
    refreshCurrentTab,
    switchTab,
    removeItem,
    clearAllData,
    getTabNavs
  }
})
